import VERTC from '@volcengine/rtc';
import openAPIs from './api';

const APP_ID = '67adf9d0d35c9e01792239ae';
const BUSINESS_ID = '';
const BotName = 'RobotMan_'; // 自定义智能体名称
const CommandKey = 'ctrl';

/**
 * 指令类型（JS 中用对象模拟枚举）
 */
const COMMAND = {
  EXTERNAL_TEXT_TO_SPEECH: 'ExternalTextToSpeech' // 发送文本转语音指令
};

/**
 * 打断优先级（JS 中用对象模拟枚举）
 */
const INTERRUPT_PRIORITY = {
  NONE: 0,       // 占位
  HIGH: 1,       // 高优先级（终止当前交互）
  MEDIUM: 2,     // 中优先级（当前交互结束后播放）
  LOW: 3         // 低优先级（空闲时播放，忙碌时丢弃）
};

/**
 * RTC Client
 */
export class RtcClient {
  constructor() {
    this.engine = null; // RTC 引擎实例
    this.config = null; // 配置参数
    this.audioBotEnabled = false; // AI Bot 是否启用
  }

  /**
   * 将字符串包装成 TLV 格式
   */
  stringToTLV(inputString, type = '') {
    const typeBuffer = new Uint8Array(4);
    // 填充类型缓冲区
    for (let i = 0; i < type.length; i++) {
      typeBuffer[i] = type.charCodeAt(i);
    }
    // 编码文本内容
    const valueBuffer = new TextEncoder().encode(inputString);
    const lengthBuffer = new Uint32Array(1);
    lengthBuffer[0] = valueBuffer.length; // 内容长度

    // 构建 TLV 缓冲区（类型4字节 + 长度4字节 + 内容n字节）
    const tlvBuffer = new Uint8Array(typeBuffer.length + 4 + valueBuffer.length);
    tlvBuffer.set(typeBuffer, 0); // 写入类型

    // 写入长度（大端字节序）
    tlvBuffer[4] = (lengthBuffer[0] >> 24) & 0xff;
    tlvBuffer[5] = (lengthBuffer[0] >> 16) & 0xff;
    tlvBuffer[6] = (lengthBuffer[0] >> 8) & 0xff;
    tlvBuffer[7] = lengthBuffer[0] & 0xff;

    tlvBuffer.set(valueBuffer, 8); // 写入内容
    return tlvBuffer.buffer;
  }

  /**
   * 初始化 RTC 引擎
   */
  createEngine(props) {
    this.config = props;
    this.engine = VERTC.createEngine(this.config.appId);
  }

  /**
   * 监听 RTC 事件
   */
  addEventListeners(events) {
    for (const event of Object.keys(events)) {
      this.engine.on(event, events[event]);
    }
  }

  /**
   * 获取音频设备列表
   */
  async getDevices() {
    const audioInputs = await VERTC.enumerateAudioCaptureDevices();
    return {
      audioInputs: audioInputs.map(input => input.deviceId)
    };
  }

  /**
   * 开启音频采集
   */
  startAudioCapture = async (mic) => {
    await this.engine.startAudioCapture(mic);
  };

  /**
   * 停止音频采集
   */
  stopAudioCapture = async () => {
    await this.engine.stopAudioCapture();
  };

  /**
   * 推流
   */
  publishStream(mediaType) {
    this.engine.publishStream(mediaType);
  }

  /**
   * 停止推流
   */
  unpublishStream(mediaType) {
    this.engine.unpublishStream(mediaType);
  }

  /**
   * 加入房间
   */
  joinRoom({ token, username }) {
    console.log('token', token);
    console.log('joinRoom config', this.config);
    return this.engine.joinRoom(
      token,
      `${this.config.roomId}`,
      {
        userId: this.config.userId,
        extraInfo: JSON.stringify({
          user_name: username || this.config.userId,
          user_id: this.config.userId
        })
      },
      {
        isAutoPublish: true, // 进房后自动推流
        isAutoSubscribeAudio: true // 自动订阅音频
      }
    );
  }

  /**
   * 离开房间
   */
  leaveRoom() {
    this.stopAudioBot(this.config.roomId, this.config.userId);
    this.engine.leaveRoom();
  }

  /**
   * 启用 AI Bot
   */
  async startAudioBot(roomId, userId, config) {
    if (this.audioBotEnabled) {
      await this.stopAudioBot(roomId, userId);
    }
    // 调用 OpenAPI 启用 AI Bot（需根据实际接口参数调整）
    await openAPIs.StartVoiceChat(config);
    this.audioBotEnabled = true;
  }

  /**
   * 停止 AI Bot
   */
  async stopAudioBot(roomId, userId) {
    if (this.audioBotEnabled) {
      await openAPIs.StopVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId
      });
      this.audioBotEnabled = false;
    }
  }

  /**
   * 打断智能体说话
   */
  async stopAudioVoice(roomId, userId) {
    if (this.audioBotEnabled) {
      const res = await openAPIs.UpdateVoiceChat({
        AppId: APP_ID,
        BusinessId: BUSINESS_ID,
        RoomId: roomId,
        TaskId: userId,
        Command: 'interrupt'
      });
      return res;
    }
    return Promise.reject(new Error('AI 打断失败：Bot 未启用'));
  }

  /**
   * 发送文本转语音（TTS）指令
   * @param {string} text - 要播报的文本内容
   * @param {number} interruptMode - 打断优先级（默认高优先级）
   */
  sendTextToSpeech(text, interruptMode = INTERRUPT_PRIORITY.HIGH) {
    if (!this.audioBotEnabled) {
      console.warn('发送失败：AI Bot 未启用');
      return;
    }

    try {
      // 构建 TTS 指令并发送
      this.engine.sendUserBinaryMessage(
        BotName,
        this.stringToTLV(
          JSON.stringify({
            Command: COMMAND.EXTERNAL_TEXT_TO_SPEECH,
            Message: text,
            InterruptMode: interruptMode
          }),
          CommandKey
        )
      );
      console.log(`TTS 指令发送成功：${text}（优先级：${interruptMode}）`);
    } catch (error) {
      console.error('TTS 指令发送失败：', error);
    }
  }
}

export default new RtcClient();